import { useCallback, useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import { AlertCircle, Archive, ArrowLeft, CheckCircle2, FileLock2, Loader2, LockKeyhole, RefreshCw, Save, Send, Sparkles, XCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Cohort, AdminPimDetail, PimDetailRecord, SourceItem, mapAdminDetail, researchData,
  safeInlinePolicyInput,
} from "./research-pipeline-contracts";
import { AdminResearchFeedbackSection } from "./admin-research-feedback";

type EditorValues = {
  sector: string; geography: string; stage: string; leadAdvisor: string; preparedDate: string;
  evaluationStatus: string; disclosureVersion: string; cardSnippet: string; campaignSnippet: string;
  executiveSummary: string; evaluationThesis: string; claims: string; evidence: string;
  evidenceGaps: string; risks: string; sources: string; estimatedReadMinutes: string;
  claimsEvidenceConfirmed: boolean; cohortIds: number[];
};

const pretty = (value: string) => value.replaceAll("_", " ");

const itemText = (items: SourceItem[]) => (items || [])
  .map((item) => item.text || item.title || item.label || (item as SourceItem & { body?: string }).body || "")
  .filter(Boolean)
  .join("\n\n");

const parseItems = (value: string, label: string): SourceItem[] => {
  const items = value.split(/\n\s*\n|\n(?=[•*-]\s+)/)
    .map((line) => line.replace(/^\s*[•*-]\s*/, "").trim())
    .filter(Boolean)
    .map((text) => ({ text }));
  if (!items.length && value.trim()) throw new Error(`${label} could not be read.`);
  return items;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'published': return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
    case 'approved': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    case 'in_review': return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
    case 'changes_requested': return 'bg-destructive/10 text-destructive border-destructive/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

function EditorField({ form, name, label, description, rows = 5 }: { form: any, name: any, label: string, description?: string, rows?: number }) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-base font-serif text-foreground">{label}</FormLabel>
          {description && <p className="text-sm text-muted-foreground mb-2">{description}</p>}
          <FormControl>
            <Textarea {...field} rows={rows} className="resize-none bg-background font-serif text-foreground leading-relaxed shadow-sm focus-visible:ring-primary" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default function AdminResearchPipelineDetail() {
  const [, params] = useRoute("/admin/research-pipeline/:id");
  const id = Number(params?.id);

  const [record, setRecord] = useState<PimDetailRecord | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showPolicyConfirmation, setShowPolicyConfirmation] = useState(false);
  const [policyConfirmed, setPolicyConfirmed] = useState(false);

  const form = useForm<EditorValues>({
    defaultValues: {
      sector: "", geography: "", stage: "", leadAdvisor: "", preparedDate: "", evaluationStatus: "",
      disclosureVersion: "", cardSnippet: "", campaignSnippet: "", executiveSummary: "", evaluationThesis: "",
      claims: "[]", evidence: "[]", evidenceGaps: "[]", risks: "[]", sources: "[]",
      estimatedReadMinutes: "", claimsEvidenceConfirmed: false, cohortIds: [],
    }
  });

  const load = useCallback(async () => {
    if (!Number.isInteger(id)) return;
    setLoading(true); setError("");
    try {
      const raw = await researchData<AdminPimDetail>(`/pims/${id}`);
      const detail = mapAdminDetail(raw);
      setCohorts(raw.availableCohorts.map((cohort) => ({ id: Number(cohort.id), name: cohort.name })));
      setRecord(detail);

      const v = detail.version;
      form.reset({
        sector: v.sector || "", geography: v.geography || "", stage: v.stage || "", leadAdvisor: v.leadAdvisor || "",
        preparedDate: v.preparedDate?.slice(0, 10) || "", evaluationStatus: v.evaluationStatus || "",
        disclosureVersion: v.disclosureVersion || "", cardSnippet: v.cardSnippet || "",
        campaignSnippet: v.campaignSnippet || "", executiveSummary: v.executiveSummary || "",
        evaluationThesis: v.evaluationThesis || "", claims: itemText(v.claims), evidence: itemText(v.evidence),
        evidenceGaps: itemText(v.evidenceGaps), risks: itemText(v.risks), sources: itemText(v.sources),
        estimatedReadMinutes: v.estimatedReadMinutes?.toString() || "",
        claimsEvidenceConfirmed: v.claimsEvidenceConfirmed, cohortIds: v.cohortIds || [],
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the research record.");
    } finally {
      setLoading(false);
    }
  }, [form, id]);

  useEffect(() => { void load(); }, [load]);

  const onAction = async (values: EditorValues, actionType: string) => {
    setError(""); setNotice("");
    setPending(actionType);

    try {
      if (["submit", "save"].includes(actionType)) {
        const body = {
          sector: values.sector || null, geography: values.geography || null, stage: values.stage || null,
          leadAdvisor: values.leadAdvisor || null,
          preparedDate: values.preparedDate ? new Date(`${values.preparedDate}T00:00:00.000Z`).toISOString() : null,
          evaluationStatus: values.evaluationStatus, disclosureVersion: values.disclosureVersion,
          cohortIds: values.cohortIds, cardSnippet: values.cardSnippet, campaignSnippet: values.campaignSnippet,
          executiveSummary: values.executiveSummary, evaluationThesis: values.evaluationThesis,
          claims: parseItems(values.claims, "Company claims"), evidence: parseItems(values.evidence, "Evidence reviewed"),
          evidenceGaps: parseItems(values.evidenceGaps, "Evidence gaps"), risks: parseItems(values.risks, "Risks"),
          sources: parseItems(values.sources, "Sources"), claimsEvidenceConfirmed: values.claimsEvidenceConfirmed,
          estimatedReadMinutes: values.estimatedReadMinutes ? Number(values.estimatedReadMinutes) : null,
        };
        await researchData(`/pims/${id}/narrative`, { method: "PUT", body: JSON.stringify(body) });
      }

      if (actionType === "submit") {
        await researchData(`/pims/${id}/submit`, { method: "POST" });
        setNotice("Submitted for approval.");
      } else if (actionType === "approve_publish") {
        await researchData(`/pims/${id}/decisions`, { method: "POST", body: JSON.stringify({ decision: "approved", comment: null }) });
        if (record?.publicationReadiness.every(item => item.ready)) {
          await researchData(`/pims/${id}/publish`, { method: "POST" });
          setNotice("Approved and published successfully.");
        } else {
          setShowPolicyConfirmation(true);
          setNotice("Version approved. Confirm the safe cohort enablement below to publish.");
        }
      } else if (actionType === "publish") {
        if (record?.publicationReadiness.every(item => item.ready)) {
          await researchData(`/pims/${id}/publish`, { method: "POST" });
          setNotice("Published successfully.");
        } else {
          setShowPolicyConfirmation(true);
        }
      } else if (actionType === "save") {
        setNotice("Draft saved successfully.");
      }

      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The action failed.");
    } finally {
      setPending("");
    }
  };

  const enableCohortsAndPublish = async () => {
    if (!record || !policyConfirmed) return;
    const missing = record.publicationReadiness.filter(item => !item.ready);
    if (!missing.length) {
      setShowPolicyConfirmation(false);
      return;
    }
    setPending("policy_publish");
    setError(""); setNotice("");
    try {
      for (const cohort of missing) {
        const policy = await researchData<{ id: number }>(`/cohort-policies`, {
          method: "POST",
          body: JSON.stringify(safeInlinePolicyInput(
            Number(cohort.cohortId),
            record.version.disclosureVersion,
            new Date().toISOString(),
          )),
        });
        await researchData(`/cohort-policies/${policy.id}/decisions`, {
          method: "POST",
          body: JSON.stringify({
            decision: "approved",
            comment: `Explicit inline activation for current and future Research Pipeline publications; no historical catalogue access.`,
          }),
        });
      }
      await researchData(`/pims/${id}/publish`, { method: "POST" });
      setShowPolicyConfirmation(false);
      setPolicyConfirmed(false);
      setNotice("Cohort access was explicitly enabled and this research was published.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Cohort enablement failed.");
      await load();
    } finally {
      setPending("");
    }
  };

  const onRequestChanges = async () => {
    const comment = window.prompt("Reason for requesting changes:");
    if (!comment) return;
    setPending("changes");
    try {
      await researchData(`/pims/${id}/decisions`, { method: "POST", body: JSON.stringify({ decision: "changes_requested", comment }) });
      setNotice("Changes requested.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The action failed.");
    } finally {
      setPending("");
    }
  };

  const onWithdraw = async () => {
    const reason = window.prompt("Required withdrawal reason:");
    if (!reason) return;
    setPending("withdraw");
    try {
      await researchData(`/pims/${id}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) });
      setNotice("Research withdrawn.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The action failed.");
    } finally {
      setPending("");
    }
  };

  const onArchive = async () => {
    if (!window.confirm("Archive this internal research record?")) return;
    setPending("archive");
    try {
      await researchData(`/${id}/archive`, { method: "POST" });
      setNotice("Record archived.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The action failed.");
    } finally {
      setPending("");
    }
  };

  if (loading && !record) {
    return <div className="flex justify-center gap-2 py-20 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading editorial desk...</div>;
  }
  if (!record) {
    return (
      <div className="space-y-4 p-8">
        <Link href="/admin/research-pipeline" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-2 inline h-4 w-4" />Research Pipeline</Link>
        <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive">{error || "Record not found."}</div>
      </div>
    );
  }

  const { pim, version } = record;
  const missingPolicies = record.publicationReadiness.filter(item => !item.ready);
  const readinessLabel = (state: typeof record.publicationReadiness[number]["state"]) => {
    switch (state) {
      case "ready": return "Ready";
      case "disabled": return "Disabled by policy";
      case "awaiting_approval": return "Policy awaiting approval";
      case "scheduled": return "Policy not active yet";
      case "expired": return "Policy expired";
      default: return "Policy not enabled";
    }
  };

  const ActionArea = () => {
    if (pim.status === "published") {
      return (
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-500/10 p-4 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 shadow-sm">
            <div className="flex items-center gap-2 font-medium mb-1">
              <CheckCircle2 className="h-5 w-5" />
              Live & Published
            </div>
            <p className="text-sm leading-relaxed">This research is visible to investors in the selected cohorts. Kavya is actively using this version.</p>
          </div>
          <div className="flex flex-col gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20" onClick={onWithdraw} disabled={!!pending}>
              <FileLock2 className="mr-2 h-4 w-4" /> Withdraw Research
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="space-y-2" aria-label="Selected cohort publication readiness">
          <div className="flex items-center gap-2 text-sm font-medium">
            <LockKeyhole className="h-4 w-4 text-primary" />
            Cohort readiness
          </div>
          {record.publicationReadiness.map(item => (
            <div key={item.cohortId} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-xs">
              <span className="font-medium text-foreground">{item.cohortName}</span>
              <span className={item.ready ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}>
                {readinessLabel(item.state)}
                {item.ready && item.activeRevision ? ` · revision ${item.activeRevision}` : ""}
              </span>
            </div>
          ))}
          {missingPolicies.length > 0 && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Publishing stays blocked until every selected cohort has an active, explicitly approved policy.
            </p>
          )}
        </div>

        <FormField control={form.control} name="claimsEvidenceConfirmed" rules={{ validate: (v) => v || "Required before submission" }} render={({ field }) => (
          <FormItem className="rounded-lg border border-border bg-muted/30 p-4 transition-colors hover:bg-muted/50">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox className="mt-0.5" checked={field.value} onCheckedChange={field.onChange} />
              <div className="text-sm">
                <p className="font-medium text-foreground">Confirm accuracy</p>
                <p className="text-muted-foreground mt-0.5">I confirm that claims and evidence gaps are accurately distinguished.</p>
              </div>
            </label>
            <FormMessage />
          </FormItem>
        )} />

        {showPolicyConfirmation && missingPolicies.length > 0 && (
          <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">Explicitly enable selected cohorts?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                This creates and records a new approved policy revision for {missingPolicies.map(item => item.cohortName).join(", ")}.
                It includes this publication and future publications only. Earlier Research Pipeline publications and campaigns remain hidden.
                Any disabled policy is replaced only by this explicit revision.
              </p>
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-xs text-foreground">
              <Checkbox checked={policyConfirmed} onCheckedChange={checked => setPolicyConfirmed(checked === true)} />
              <span>I confirm current-and-future access with no historical catalogue or campaign access.</span>
            </label>
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={() => void enableCohortsAndPublish()} disabled={!record.canApproveResearchPolicies || !policyConfirmed || !!pending}>
                {pending === "policy_publish" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Enable & Publish
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setShowPolicyConfirmation(false); setPolicyConfirmed(false); }} disabled={!!pending}>
                Cancel
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {record.canApproveResearchPolicies
                ? "Your authenticated PC/Bharat account will be recorded as the single approving decision."
                : "Only authenticated PC or Bharat can approve this policy revision. Ask either approver to complete this confirmation."}
            </p>
          </div>
        )}

        {pim.status === "in_review" ? (
          <div className="space-y-2">
            <Button type="button" className="w-full" onClick={form.handleSubmit((v) => onAction(v, 'approve_publish'))} disabled={!!pending}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Approve & Publish
            </Button>
            <Button type="button" variant="ghost" className="w-full text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onRequestChanges} disabled={!!pending}>
              <XCircle className="mr-2 h-4 w-4" /> Request Changes
            </Button>
          </div>
        ) : pim.status === "approved" ? (
          <div className="space-y-2">
            <Button type="button" className="w-full" onClick={form.handleSubmit((v) => onAction(v, 'publish'))} disabled={!!pending}>
              <Send className="mr-2 h-4 w-4" /> Publish Now
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Button type="button" className="w-full" onClick={form.handleSubmit((v) => onAction(v, 'submit'))} disabled={!!pending}>
              <Send className="mr-2 h-4 w-4" /> Save & Submit for Approval
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={form.handleSubmit((v) => onAction(v, 'save'))} disabled={!!pending}>
              <Save className="mr-2 h-4 w-4" /> Save Draft
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-[100dvh] bg-background pb-20">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-4 shadow-sm backdrop-blur-md sm:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <Link href="/admin/research-pipeline" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="h-6 w-px bg-border" />
          <div className="min-w-0">
            <h1 className="flex min-w-0 items-center gap-2 font-serif text-lg font-medium text-foreground sm:gap-3 sm:text-xl">
              <span className="truncate">{pim.companyName}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(pim.status)} font-sans capitalize tracking-wide`}>
                {pretty(pim.status)}
              </span>
            </h1>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="text-muted-foreground hover:text-foreground">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </header>

      <main className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">
        {error && <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2 shadow-sm"><AlertCircle className="h-4 w-4" />{error}</div>}
        {notice && <div className="mb-6 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2 shadow-sm"><CheckCircle2 className="h-4 w-4" />{notice}</div>}

        <Form {...form}>
          <form className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8">

            {/* LEFT COLUMN: EDITORIAL DESK */}
            <div className="space-y-8">

              {/* Meera Banner */}
              {pim.status !== 'published' && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-2 text-primary shadow-inner">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground">Meera AI Analysis</h3>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                        Run Meera to analyze the uploaded document (<span className="font-mono text-xs text-foreground bg-muted px-1 py-0.5 rounded">{version.sourceFilename}</span>) and draft this brief.
                        <strong className="block mt-1 text-foreground">Kavya's cohort intelligence is updated immediately when this completes.</strong>
                      </p>
                      <Button
                        type="button"
                        onClick={() => {
                          setPending("ai");
                          researchData(`/pims/${id}/ai-draft`, { method: "POST" })
                            .then(() => { setNotice("Meera draft generated successfully."); load(); })
                            .catch(e => setError(e instanceof Error ? e.message : "AI generation failed."))
                            .finally(() => setPending(""));
                        }}
                        disabled={!!pending}
                        className="mt-4 shadow-sm transition-all"
                      >
                        {pending === "ai" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                        Run Meera
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Editor Content */}
              <fieldset disabled={["in_review", "approved", "published"].includes(pim.status)} className="space-y-8">
                <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <div className="border-b border-border bg-muted/30 px-6 py-4">
                    <h2 className="font-serif text-lg font-medium text-foreground">Research Narrative</h2>
                    <p className="text-sm text-muted-foreground mt-1">Edit the research in plain language. Put each separate point on a new line.</p>
                  </div>
                  <div className="p-6 space-y-8">
                    <EditorField form={form} name="executiveSummary" label="Executive Summary" />
                    <EditorField form={form} name="evaluationThesis" label="Why this entered the pipeline" />
                    <EditorField form={form} name="claims" label="What the company claims" />
                    <EditorField form={form} name="evidence" label="What we have seen" />
                    <EditorField form={form} name="evidenceGaps" label="What remains unproven" />
                    <EditorField form={form} name="risks" label="What could break the case" />
                    <EditorField form={form} name="sources" label="Sources and further reading" />
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <div className="border-b border-border bg-muted/30 px-6 py-4">
                    <h2 className="font-serif text-lg font-medium text-foreground">Summary Snippets</h2>
                  </div>
                  <div className="p-6 space-y-6">
                    <EditorField form={form} name="cardSnippet" label="Preview snippet (Short)" rows={2} />
                    <EditorField form={form} name="campaignSnippet" label="Email snippet" rows={3} />
                  </div>
                </div>
              </fieldset>
            </div>

            {/* RIGHT COLUMN: METADATA & ACTIONS */}
            <div className="space-y-6 lg:sticky lg:top-24">

              {/* Publishing Action Card */}
              <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                <div className="bg-primary px-6 py-4 text-primary-foreground">
                  <h2 className="font-serif text-lg font-medium">Publishing Desk</h2>
                </div>
                <div className="p-6">
                  <ActionArea />
                </div>
              </div>

              <fieldset disabled={["in_review", "approved", "published"].includes(pim.status)} className="space-y-6">
                {/* Cohorts */}
                <div className="rounded-xl border border-border bg-card shadow-sm">
                  <div className="border-b border-border bg-muted/30 px-5 py-3">
                    <h3 className="font-medium text-foreground">Target Cohorts</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">Publication controls investor visibility for the selected cohorts. Kavya receives updates regardless of publication.</p>
                    {["in_review", "approved"].includes(pim.status) && (
                      <p className="mb-4 text-xs text-muted-foreground">Targets are locked to this reviewed revision. Return to a draft before changing them.</p>
                    )}
                    <FormField control={form.control} name="cohortIds" rules={{ validate: (v) => v.length > 0 || "Select at least one cohort" }} render={({ field }) => (
                      <FormItem>
                        <div className="space-y-2">
                          {cohorts.map((cohort) => (
                            <label key={cohort.id} className={`flex items-center gap-3 rounded-lg border p-3 text-sm cursor-pointer transition-colors ${field.value.includes(cohort.id) ? 'bg-primary/5 border-primary/30' : 'bg-card border-border hover:bg-muted/50'}`}>
                              <Checkbox checked={field.value.includes(cohort.id)} onCheckedChange={(checked) => {
                                field.onChange(checked ? [...field.value, cohort.id] : field.value.filter(x => x !== cohort.id));
                              }} />
                              <span className={`font-medium ${field.value.includes(cohort.id) ? 'text-primary' : 'text-foreground'}`}>{cohort.name}</span>
                            </label>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                {/* Metadata */}
                <div className="rounded-xl border border-border bg-card shadow-sm">
                  <div className="border-b border-border bg-muted/30 px-5 py-3">
                    <h3 className="font-medium text-foreground">Metadata</h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <FormField control={form.control} name="sector" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Sector</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="geography" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Geography</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="stage" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Stage</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="leadAdvisor" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Lead Advisor</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="preparedDate" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Prepared Date</FormLabel><FormControl><Input type="date" className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="estimatedReadMinutes" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Read Mins</FormLabel><FormControl><Input type="number" className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                    </div>
                    <div className="pt-3 border-t border-border mt-1 grid gap-3">
                      <FormField control={form.control} name="evaluationStatus" rules={{ required: "Required" }} render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Evaluation Status</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                      <FormField control={form.control} name="disclosureVersion" rules={{ required: "Required" }} render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">Disclosure Reference</FormLabel><FormControl><Input className="h-8 text-sm bg-muted/50" {...field} /></FormControl></FormItem>} />
                    </div>
                  </div>
                </div>
              </fieldset>

              {id > 0 && <AdminResearchFeedbackSection pimId={id} />}

              {/* Advanced Actions */}
              {!["published", "archived"].includes(pim.status) && (
                <div className="flex justify-end gap-2 px-2">
                  <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive transition-colors" onClick={onArchive} disabled={!!pending}>
                    <Archive className="mr-2 h-4 w-4" /> Archive Record
                  </Button>
                </div>
              )}

            </div>
          </form>
        </Form>
      </main>
    </div>
  );
}
